import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
	Box,
	Button,
	IconButton,
	MenuItem,
	TextField,
	Typography,
	Divider,
	makeStyles,
	Checkbox,
	FormControlLabel,
	FormLabel,
	Collapse,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import TextIcon from "@material-ui/icons/TextFields";
import DateRangeIcon from "@material-ui/icons/DateRange";
import DialpadIcon from "@material-ui/icons/Dialpad";
import DehazeIcon from "@material-ui/icons/Dehaze";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import BorderColorIcon from "@material-ui/icons/BorderColor";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import { FieldModel, FieldType, Group } from "./types";
// import { typeOfDocuments } from "../utils/typeOfDocuments";
import { typeOfDocumentsLabels } from "../utils/typeOfDocuments";
import { FieldGroups } from "./FieldGroups";
import { groupColorService } from "./groupColorService";
import { getTemplateFieldGroups } from "../../../services/templateV2Service";
export const fieldTypes = [
	{
		label: "Short Text",
		value: "shortText",
		icon: <TextIcon style={{ color: "rgba(0, 106, 198, 1)" }} />,
		type: typeOfDocumentsLabels.text,
	},
	{
		label: "Date",
		value: "date",
		icon: <DateRangeIcon style={{ color: "rgba(0, 106, 198, 1)" }} />,
		type: typeOfDocumentsLabels.date,
	},
	{
		label: "Number",
		value: "number",
		icon: <DialpadIcon style={{ color: "rgba(0, 106, 198, 1)" }} />,
		type: typeOfDocumentsLabels.text,
	},
	{
		label: "Select",
		value: "select",
		icon: <DehazeIcon style={{ color: "rgba(0, 106, 198, 1)" }} />,
		type: typeOfDocumentsLabels.select,
	},
	{
		label: "Checkbox",
		value: "checkbox",
		icon: <CheckBoxIcon style={{ color: "rgba(0, 106, 198, 1)" }} />,
		type: typeOfDocumentsLabels.checkbox,
	},
	{
		label: "Radio",
		value: "radio",
		icon: <RadioButtonCheckedIcon style={{ color: "rgba(0, 106, 198, 1)" }} />,
		type: typeOfDocumentsLabels.radio,
	},
	{
		label: "Signature",
		value: "signature",
		icon: <BorderColorIcon style={{ color: "rgba(0, 106, 198, 1)" }} />,
		type: typeOfDocumentsLabels.signature,
	},
	{
		label: "Initials",
		value: "initials",
		icon: <EditIcon style={{ color: "rgba(0, 106, 198, 1)" }} />,
		type: typeOfDocumentsLabels.initials,
	},
];
const useStyles = makeStyles((theme) => ({
	fieldHeaderCard: {
		display: "flex",
		gap: 12,
		alignItems: "center",
		backgroundColor: "#f7f9fc",
		padding: "10px 10px",
		borderRadius: 8,
		border: "1px solid rgba(0,0,0,0.08)",
		marginBottom: 12,
		[theme.breakpoints.down("md")]: {
			padding: "8px 8px",
			gap: 10,
		},
	},
	fieldHeaderIcon: {
		width: 36,
		height: 36,
		borderRadius: 10,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "rgba(0, 106, 198, 0.12)",
		flexShrink: 0,
	},
	fieldHeaderTitleRow: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: 8,
		width: "100%",
	},
	sectionHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "10px 8px",
		borderRadius: 8,
		cursor: "pointer",
		userSelect: "none",
		"&:hover": {
			backgroundColor: "rgba(0,0,0,0.04)",
		},
		[theme.breakpoints.down("md")]: {
			padding: "8px 6px",
		},
	},
	sectionHeaderLeft: {
		display: "flex",
		alignItems: "center",
		gap: 8,
	},
	sectionBody: {
		padding: "6px 8px 12px 8px",
		[theme.breakpoints.down("md")]: {
			padding: "6px 6px 10px 6px",
		},
	},
	fieldTypeContainer: {
		marginTop: 12,
		[theme.breakpoints.down("md")]: {
			marginTop: 10,
		},
	},
	fieldTypeSelect: {
		marginTop: 6,
	},
	buttonDeleteContainer: {
		marginTop: 12,
		display: "flex",
		justifyContent: "flex-end",
		flex: 1,
	},
	buttonDelete: {
		alignSelf: "flex-end",
	},
}));

export interface InspectorProps {
	field?: FieldModel;
	update: (p: Partial<FieldModel>) => void;
	onClose: () => void;
	onDelete?: () => void;
	groups?: Group[];
	onGroupsChange?: (groups: Group[]) => void;
	onAssignToGroup?: (fieldId: string, groupId: string | undefined) => void;
	templateId?: string;
	allFields?: FieldModel[];
}

export const Inspector: React.FC<InspectorProps> = ({
	field,
	update,
	onClose,
	onDelete,
	groups = [],
	onGroupsChange,
	onAssignToGroup,
	templateId,
	allFields = [],
}) => {
	const classes = useStyles();
	const [showGroupsCreation, setShowGroupsCreation] = useState(false);
	const [apiGroups, setApiGroups] = useState<Group[]>([]);
	const [, setIsLoadingGroups] = useState(false);
	const [fieldGroupMapping, setFieldGroupMapping] = useState<Record<number, string>>({});
	const [hasAutoAssignedGroup, setHasAutoAssignedGroup] = useState(false);
	const [sectionsOpen, setSectionsOpen] = useState({
		properties: true,
		assignment: false,
		constraints: true,
	});
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

	useEffect(() => {
		if (!deleteConfirmOpen) return;
		const t = window.setTimeout(() => setDeleteConfirmOpen(false), 3000);
		return () => window.clearTimeout(t);
	}, [deleteConfirmOpen]);

	const hasField = !!field;

	// Build a lookup of group colors based on persisted field data
	const fieldGroupColorMap = React.useMemo(() => {
		const colorMap = new Map<string, string>();
		if (Array.isArray(allFields)) {
			allFields.forEach((currentField) => {
				if (
					currentField &&
					currentField.fieldGroup &&
					currentField.fieldGroupColor &&
					!colorMap.has(currentField.fieldGroup)
				) {
					colorMap.set(currentField.fieldGroup, currentField.fieldGroupColor);
				}
			});
		}
		return colorMap;
	}, [allFields]);

	// Use API groups if available, otherwise fall back to props groups
	// Ensure we always have an array, memoized for stable deps
	const availableGroups = useMemo(() => {
		return Array.isArray(apiGroups) && apiGroups.length > 0
			? apiGroups
			: (Array.isArray(groups) ? groups : []);
	}, [apiGroups, groups]);

	const loadGroupsFromAPI = useCallback(async () => {
		if (!templateId) return;

		try {
			setIsLoadingGroups(true);
			const response = await getTemplateFieldGroups(templateId);

			if (response.status === 200 && response.data && response.data.success) {
				let nextGroups: Group[] = [];

				if (response.data.data && response.data.data.groups) {
					const groupsObject = response.data.data.groups;

					// Create field-to-group mapping
					const fieldToGroupMap: Record<number, string> = {};
					Object.keys(groupsObject).forEach((groupName) => {
						const fieldIds = groupsObject[groupName];
						if (Array.isArray(fieldIds)) {
							fieldIds.forEach((fieldId) => {
								fieldToGroupMap[fieldId] = groupName;
							});
						}
					});
					setFieldGroupMapping(fieldToGroupMap);

					// Convert the groups object to an array format expected by the UI
					const sortedGroupNames = Object.keys(groupsObject).sort();
					nextGroups = sortedGroupNames.map((groupName, index) => {
						const groupId = `group-${index + 1}-${groupName.toLowerCase().replace(/\s+/g, '-')}`;
						const colorIndex = index % groupColorService.getAllColors().length;
						const persistedGroupColor = fieldGroupColorMap.get(groupName);
						const colorScheme = persistedGroupColor
							? groupColorService.getColorByPrimaryColor(persistedGroupColor)
							: groupColorService.getColorByIndex(colorIndex);

						return { id: groupId, name: groupName, color: colorScheme.primary };
					});
				}

				setApiGroups(nextGroups);
				if (onGroupsChange && Array.isArray(nextGroups)) onGroupsChange(nextGroups);
			}
		} catch (error) {
			console.error("Error loading field groups from API:", error);
		} finally {
			setIsLoadingGroups(false);
		}
	}, [templateId, onGroupsChange, fieldGroupColorMap]);

	// Load groups from field-groups API when templateId changes
	useEffect(() => {
		if (templateId) loadGroupsFromAPI();
	}, [templateId, loadGroupsFromAPI]);

	// Normalize groups so their colors align with persisted field colors when available
	const normalizedGroups = React.useMemo(() => {
		if (!Array.isArray(availableGroups)) {
			return [];
		}
		return availableGroups.map((group) => {
			if (!group) {
				return group;
			}
			const persistedColor = group.name ? fieldGroupColorMap.get(group.name) : undefined;
			if (persistedColor && persistedColor !== group.color) {
				return { ...group, color: persistedColor };
			}
			return group;
		});
	}, [availableGroups, fieldGroupColorMap]);

	// Debug logging
	console.log("Inspector - Field:", field);
	console.log("Inspector - API Groups:", apiGroups, "Is Array:", Array.isArray(apiGroups));
	console.log("Inspector - Props Groups:", groups, "Is Array:", Array.isArray(groups));
	console.log("Inspector - Available Groups:", availableGroups, "Is Array:", Array.isArray(availableGroups));
	console.log("Inspector - Field fieldGroup:", field?.fieldGroup);

	// Check if the field's fieldGroup matches any available group
	const matchingGroup = field?.fieldGroup && Array.isArray(normalizedGroups)
		? normalizedGroups.find(g => g && g.name === field.fieldGroup)
		: null;
	console.log("Inspector - Matching group:", matchingGroup);

	// Reset auto-assignment flag when field changes
	useEffect(() => {
		setHasAutoAssignedGroup(false);
	}, [field?.id]);

	// Auto-assign field group based on API field-group mapping
	useEffect(() => {
		if (field?.fieldId && Object.keys(fieldGroupMapping).length > 0 && !hasAutoAssignedGroup) {
			const assignedGroupName = fieldGroupMapping[field.fieldId];

			if (assignedGroupName && (!field?.fieldGroup || field.fieldGroup !== assignedGroupName)) {
				const assignedGroup = Array.isArray(normalizedGroups)
					? normalizedGroups.find(g => g && g.name === assignedGroupName)
					: undefined;
				const fieldGroupColor = assignedGroup ? assignedGroup.color : undefined;

				update({ fieldGroup: assignedGroupName, fieldGroupColor });
				setHasAutoAssignedGroup(true);
			}
		}
		// Intentionally omit frequently-changing dependencies to avoid reassign loops
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [field?.fieldId, fieldGroupMapping]);

	// Helper function to assign current field to a group
	const assignFieldToGroup = useCallback((groupId: string | undefined) => {
		if (!field) return;
		// Find the group name and color from the group ID
		const group = Array.isArray(normalizedGroups)
			? normalizedGroups.find(g => g && g.id === groupId)
			: undefined;
		const fieldGroup = group ? group.name : undefined;
		const fieldGroupColor = group ? group.color : undefined;

		update({ fieldGroup, fieldGroupColor });
		if (onAssignToGroup) {
			onAssignToGroup(field.id, groupId);
		}
	}, [normalizedGroups, update, onAssignToGroup, field]);

	// Expose the assignment function globally for external access
	React.useEffect(() => {
		if (field && window) {
			(window as any).assignCurrentFieldToGroup = assignFieldToGroup;
		}
	}, [field, assignFieldToGroup]);

	// Derived UI state (must be defined before render)
	const fieldTypeMeta = fieldTypes.find((t) => t.value === (field?.type ?? "shortText"));
	const fieldTypeLabel = fieldTypeMeta?.label ?? (field?.type ?? "shortText");
	const fieldTypeIcon =
		fieldTypeMeta?.icon ??
		<TextIcon style={{ color: "rgba(0, 106, 198, 1)" }} />;

	const toggleSection = useCallback((key: keyof typeof sectionsOpen) => {
		setSectionsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
	}, []);

	if (!hasField) {
		return (
			<Box width={"100%"} p={2} display="flex" flexDirection="column" height="100%">
				<Typography variant="body1">Select a field</Typography>
			</Box>
		);
	}

	return (
		<Box
			width={"100%"}
			p={2}
			display="flex"
			flexDirection="column"
			// Avoid forcing 100% height in narrow sidebars; it can cause
			// collapse sections to overlap when the parent height is auto.
			height="auto"
		>
			<div className={classes.fieldHeaderCard}>
				<div className={classes.fieldHeaderIcon}>{fieldTypeIcon}</div>
				<div style={{ flex: 1, minWidth: 0 }}>
					<div className={classes.fieldHeaderTitleRow}>
						<div style={{ minWidth: 0 }}>
							<Typography
								variant="subtitle1"
								style={{ fontWeight: 600, lineHeight: 1.2 }}
								noWrap
							>
								{field.title || "Untitled field"}
							</Typography>
							<Typography
								variant="caption"
								style={{ color: "rgba(0,0,0,0.6)" }}
								noWrap
							>
								{fieldTypeLabel} field
							</Typography>
						</div>
						<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
							{onDelete && !deleteConfirmOpen && (
								<Button
									size="small"
									variant="text"
									color="secondary"
									onClick={() => setDeleteConfirmOpen(true)}
									style={{ textTransform: "none" }}
								>
									Delete
								</Button>
							)}
							{onDelete && deleteConfirmOpen && (
								<Button
									size="small"
									variant="contained"
									color="secondary"
									onClick={() => {
										onDelete();
										setDeleteConfirmOpen(false);
									}}
								>
									Confirm
								</Button>
							)}
							<IconButton size="small" onClick={onClose} aria-label="Close inspector">
								<CloseIcon fontSize="small" />
							</IconButton>
						</div>
					</div>
				</div>
			</div>

			<div
				className={classes.sectionHeader}
				onClick={() => toggleSection("properties")}
				role="button"
				tabIndex={0}
			>
				<div className={classes.sectionHeaderLeft}>
					{sectionsOpen.properties ? (
						<ExpandMoreIcon fontSize="small" />
					) : (
						<ChevronRightIcon fontSize="small" />
					)}
					<Typography variant="subtitle2" style={{ fontWeight: 600 }}>
						Field properties
					</Typography>
				</div>
			</div>
			<Collapse in={sectionsOpen.properties} timeout="auto" unmountOnExit style={{ width: "100%" }}>
				<div className={classes.sectionBody}>
					<div className={classes.fieldTypeContainer}>
						<FormLabel>Field title</FormLabel>
						<TextField
							label=""
							variant="outlined"
							value={field.title}
							onChange={(e) => update({ title: e.target.value })}
							margin="normal"
							fullWidth
							className={classes.fieldTypeSelect}
						/>
					</div>

					<div className={classes.fieldTypeContainer}>
						<FormLabel>Field type</FormLabel>
						<TextField
							select
							label=""
							variant="outlined"
							value={field.type}
							onChange={(e) => update({ type: e.target.value as FieldType })}
							margin="normal"
							fullWidth
							className={classes.fieldTypeSelect}
							InputProps={{
								style: {
									paddingTop: 10,
									padding: 0,
									paddingRight: 0,
								},
							}}
							SelectProps={{
								style: {
									paddingTop: 10,
									padding: 0,
									paddingRight: 0,
								},
							}}
						>
							{fieldTypes.map((fieldType) => (
								<MenuItem key={fieldType.value} value={fieldType.value}>
									<span
										style={{
											display: "flex",
											alignItems: "center",
											paddingTop: 10,
										}}
									>
										{fieldType.icon}
										<Typography variant="body2" style={{ marginLeft: 8 }}>
											{fieldType.label}
										</Typography>
									</span>
								</MenuItem>
							))}
						</TextField>
					</div>

					<FormControlLabel
						control={
							<Checkbox
								name="mandatory"
								checked={field.required || false}
								onChange={(e) => update({ required: e.target.checked })}
							/>
						}
						label="Mandatory field"
					/>
					{field.type === "initials" && (
						<FormControlLabel
							control={
								<Checkbox
									name="autoGenerateInitials"
									checked={field.autoGenerateInitials || false}
									onChange={(e) =>
										update({ autoGenerateInitials: e.target.checked })
									}
								/>
							}
							label="Auto-fill initials from signer name when the form is opened"
						/>
					)}
				</div>
			</Collapse>

			<Divider style={{ marginTop: 8, marginBottom: 4 }} />

			{/* Select para signatureRole si el tipo es signature */}
			{/* <div className={classes.fieldTypeContainer}>
					<FormLabel>{ field.type === 'signature' ? "Signature Role" : "Role" }</FormLabel>
					<TextField
						select
						variant="outlined"
						value={field.signatureRole || ''}
						onChange={e => update({ signatureRole: e.target.value as 'sender' | 'recipient' })}
						margin="normal"
						fullWidth
					>
						<MenuItem value="sender">Sender</MenuItem>
						<MenuItem value="recipient">Recipient</MenuItem>
					</TextField>
				</div> */}
			{/* Constraints intentionally removed until enforced by backend/UI */}
			{/* <div className={classes.fieldTypeContainer}>
				<FormLabel>Placeholder</FormLabel>
				<TextField
					label=""
					variant="outlined"
					value={field.placeholder || ""}
					onChange={(e) => update({ placeholder: e.target.value })}
					margin="normal"
					fullWidth
				/>
			</div> */}

			<div
				className={classes.sectionHeader}
				onClick={() => toggleSection("assignment")}
				role="button"
				tabIndex={0}
				style={{ marginTop: 4 }}
			>
				<div className={classes.sectionHeaderLeft}>
					{sectionsOpen.assignment ? (
						<ExpandMoreIcon fontSize="small" />
					) : (
						<ChevronRightIcon fontSize="small" />
					)}
					<Typography variant="subtitle2" style={{ fontWeight: 600 }}>
						Assignment & groups
					</Typography>
				</div>
			</div>
			<Collapse in={sectionsOpen.assignment} timeout="auto" unmountOnExit style={{ width: "100%" }}>
				<div className={classes.sectionBody}>
					{/* Group Selection Dropdown */}
					{groups.length > 0 && (
						<div className={classes.fieldTypeContainer}>
						<FormLabel>Assigned group</FormLabel>
						<TextField
							select
							label=""
							id="field-group-select"
							variant="outlined"
							value={(() => {
								// Find group ID based on field's group name, or default to "No group" (empty string)
								if (field.fieldGroup && Array.isArray(normalizedGroups)) {
									const group = normalizedGroups.find(g => g && g.name === field.fieldGroup);
									return group ? group.id : "";
								}
								return ""; // Default to "No group" instead of first available group
							})()}
							onChange={(e) => {
								const selectedGroupId = e.target.value || undefined;
								assignFieldToGroup(selectedGroupId);
							}}
							margin="normal"
							fullWidth
							className={classes.fieldTypeSelect}
						>
							<MenuItem value="" style={{ padding: '8px 16px' }}>
								<Box display="flex" alignItems="center" width="100%" style={{ minHeight: '20px', marginTop: '0px' }}>
									<Typography variant="body2" style={{ fontStyle: 'italic', color: '#999', lineHeight: 1.5, marginLeft: '20px' }}>
										No group
									</Typography>
								</Box>
							</MenuItem>
							{Array.isArray(normalizedGroups) && normalizedGroups.map((group) => {
								if (!group || !group.id) return null;
								const colorScheme = groupColorService.getGroupColor(group);
								return (
									<MenuItem key={group.id} value={group.id} style={{ padding: '8px 16px' }}>
										<Box display="flex" alignItems="center" width="100%" style={{ minHeight: '20px', marginTop: '0px' }}>
											<Box
												width={10}
												height={10}
												borderRadius="50%"
												bgcolor={colorScheme.primary}
												border={`1px solid ${colorScheme.border}`}
												marginRight={1.5}
												flexShrink={0}
												style={{ marginTop: '1px' }}
											/>
											<Typography variant="body2" style={{ lineHeight: 1.5 }}>
												{group.name}
											</Typography>
										</Box>
									</MenuItem>
								);
							})}
						</TextField>
					</div>
				)}

				{/* Groups Creation Section */}
				{!showGroupsCreation ? (
					<div className={classes.fieldTypeContainer}>
						<Button
							variant="outlined"
							startIcon={<AddIcon />}
							onClick={() => setShowGroupsCreation(true)}
							style={{
								textTransform: 'none',
								borderColor: '#ddd',
								color: '#666',
								fontSize: '12px',
								padding: '6px 12px'
							}}
							fullWidth
						>
							Add group
						</Button>
					</div>
				) : (
					<div className={classes.fieldTypeContainer}>
						<Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
							<FormLabel>Manage groups</FormLabel>
							<IconButton
								size="small"
								onClick={() => setShowGroupsCreation(false)}
								style={{ padding: '4px' }}
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Box>
						<FieldGroups groups={availableGroups} onGroupsChange={(updatedGroups) => {
							// Update local state
							setApiGroups(updatedGroups);
							// Call parent callback if available
							if (onGroupsChange) {
								onGroupsChange(updatedGroups);
							}
						}} />
					</div>
				)}
				</div>
			</Collapse>
			{/* Delete moved to sticky action bar (DocumentEditor) */}
		</Box>
	);
};
